import { Period, Utils } from "revlock-webutils";
import ItemType from "../reference/ItemType";
import RecognitionRule from "../reference/RecognitionRule";
import { Codes } from "../ErrorCode";
import moment from "moment";

export default class RevenueDeliveryDateHelper {
    static populateDeliveryDates(
        salesOrder,
        revenueArrangement,
        soi,
        ssp,
        rai,
        context,
        arrangementContext
    ) {
        const { raItemsBySoId } = arrangementContext;
        const {
            errors,
            isApiCall,
            endDateInclusive,
            dereferenceRuleHasPriority,
            disableProduct
        } = context;
        const customFields = context.cFields;

        soi.isActive = false;
        let baseSalesOrderItems;
        let parentRaItem;

        if (soi.parentSalesOrderItemId) {
            parentRaItem = raItemsBySoId[soi.parentSalesOrderItemId];
        }

        const getMinDate = (key, productId) => {
            let date;
            baseSalesOrderItems = salesOrder.salesOrderItem;
            for (let item of baseSalesOrderItems) {
                const serviceDate = item[key];
                if (
                    !item.product ||
                    item.product.id != productId ||
                    item.product.entityType !== "BASE"
                )
                    continue;
                if (!date || Period.isAfter(date, serviceDate)) {
                    date = serviceDate;
                }
            }

            return date;
        };

        const getMaxDate = (key, productId) => {
            let date;
            baseSalesOrderItems = salesOrder.salesOrderItem;
            for (let item of baseSalesOrderItems) {
                const serviceDate = item[key];
                if (
                    !item.product ||
                    item.product.id != productId ||
                    item.product.entityType !== "BASE"
                )
                    continue;
                if (!date || Period.isAfter(serviceDate, date)) {
                    date = serviceDate;
                }
            }

            return date;
        };

        const lookup = (dateField, lookupSalesOrderItem) => {
            if (
                dateField.match(/Order Start Date/gi) ||
                dateField.match(/Order Date/gi)
            ) {
                return salesOrder.orderDate;
            }
            //special case for order start date

            // special case po items
            if (dateField == "Min Start Date") {
                return getMinDate("startDate", soi.product.parent);
            } else if (dateField == "Max Start Date") {
                return getMaxDate("startDate", soi.product.parent);
            } else if (dateField == "Min End Date") {
                return getMinDate("endDate", soi.product.parent);
            } else if (dateField == "Max End Date") {
                return getMaxDate("endDate", soi.product.parent);
            }

            let lookupField = dateField;

            const cf =
                customFields &&
                customFields.find((c) => c.displayName === dateField);
            if (cf) {
                lookupField = cf.name;
            }

            if (salesOrder[lookupField]) {
                return salesOrder[lookupField];
            }

            if (
                salesOrder.orderAttributes &&
                salesOrder.orderAttributes[lookupField]
            ) {
                return salesOrder.orderAttributes[lookupField];
            }

            if (
                lookupSalesOrderItem &&
                soi.attributes &&
                soi.attributes[lookupField]
            ) {
                return soi.attributes[lookupField];
            }

            return null;
        };

        const productStartDate = () => {
            if (
                !soi.product ||
                !soi.product.serviceStartDate ||
                !soi.product.serviceStartDate.trim()
            )
                return null;

            const {
                serviceStartDate,
                serviceStartOffset,
                serviceStartOffsetPeriodUnit
            } = soi.product;

            // salesOrderItem is po item of credit note then use parent credit note's dates directly.
            if (soi.product.entityType === "PO" && soi.isCreditNote) {
                const parentSalesOrderItem = salesOrder.salesOrderItem.find(
                    (item) => item.id === soi.parentSalesOrderItemId
                );
                return parentSalesOrderItem && parentSalesOrderItem.startDate;
            }

            let startDate = Utils.isDate(serviceStartDate)
                ? serviceStartDate
                : lookup(serviceStartDate);

            if (startDate) {
                if (serviceStartOffset) {
                    startDate = moment(startDate, "YYYY-MM-DD").add(
                        serviceStartOffset,
                        serviceStartOffsetPeriodUnit
                    );
                }
                return Utils.formatDate(startDate);
            }

            return null;
        };

        const searchForDateField = (searchField, lookupSalesOrderItem) => {
            if (
                !searchField ||
                !searchField.trim() ||
                searchField === "manualDate"
            )
                return null;

            return moment.isDate(searchField)
                ? searchField
                : lookup(searchField, lookupSalesOrderItem);
        };

        const salesOrderDereferenceStartDate = () =>
            searchForDateField(soi.dereferencedStartDate);
        const sspStartDate = (lookupSalesOrderItem) =>
            searchForDateField(
                ssp && ssp.deliveryStartDate,
                lookupSalesOrderItem
            );

        // In case dereference rules needs to take priority over user selected date,
        // We delete the date and attemp to apply dereference rules.
        let manualStartDate = undefined;
        if (
            dereferenceRuleHasPriority &&
            soi.dereferencedStartDate === "manualDate"
        ) {
            manualStartDate = soi.startDate;
            delete soi.startDate;
            delete soi.dereferencedStartDate;
        }

        // We allow overriding the start date on the UI by specifying a de-referencing field.
        // You can also override by providing it in the SSP or the product
        if (soi.dereferencedStartDate !== "manualDate") {
            // If dereference source for start date is not salesOrder clear it.
            if (
                soi.dereferencedStartDateSource &&
                soi.dereferencedStartDateSource !== "salesOrder"
            ) {
                soi.dereferencedStartDate = undefined;
            }

            // Always reset dereference start date value as we will recalcualte it.
            delete soi.dereferencedStartDateValue;
            delete soi.startDate;

            //0: override at the sales order item level if the user has specified a value
            //to use for a de-reference lookup
            if (
                soi.dereferencedStartDateSource == "salesOrder" &&
                soi.dereferencedStartDate
            ) {
                soi.startDate = salesOrderDereferenceStartDate();
            }
            //1. override at the SSP or product level if the user
            //has not specified a date
            else if (ssp && ssp.deliveryStartDate) {
                soi.startDate = sspStartDate(true);
                soi.dereferencedStartDate = ssp.deliveryStartDate;
                soi.dereferencedStartDateSource = "ssp";
            } else if (soi.product && soi.product.serviceStartDate) {
                soi.startDate = productStartDate();
                soi.dereferencedStartDate = soi.product.serviceStartDate;
                soi.dereferencedStartDateSource = "product";
                if (
                    !soi.isCreditNote &&
                    Utils.isDate(soi.product.serviceStartDate)
                ) {
                    soi.dereferencedStartDate = "Absolute Date";
                }
            } else {
                soi.dereferencedStartDateValue = undefined;
                soi.dereferencedStartDateSource = undefined;
            }
        }

        // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
        // or from UI
        if (dereferenceRuleHasPriority && !soi.startDate && manualStartDate) {
            soi.startDate = manualStartDate;
            soi.dereferencedStartDate = "manualDate";
        }

        if (!soi.startDate && parentRaItem) {
            const { salesOrderItem: parentSoi } = parentRaItem;
            if (parentSoi) {
                soi.startDate = parentSoi.startDate;
                soi.dereferencedStartDate = parentSoi.dereferencedStartDate;
            }
        }

        if (soi.startDate) {
            // We were able to get a good start date
            soi.isActive = true;
            soi.dereferencedStartDateValue = soi.startDate;
        }

        if (!soi.startDate) {
            //we set a default date here as no date here causes a publishing issuer or something
            //note that the order itself is inactive.
            soi.startDate = salesOrder.orderDate;
        }

        const productEndDate = () => {
            if (
                !soi.product ||
                !soi.product.serviceEndDate ||
                !soi.product.serviceEndDate.trim()
            )
                return null;

            const {
                serviceEndDate,
                serviceEndOffset,
                serviceEndOffsetPeriodUnit
            } = soi.product;

            // salesOrderItem is po item of credit note then use parent credit note's dates directly.
            if (soi.product.entityType === "PO" && soi.isCreditNote) {
                const parentSalesOrderItem = salesOrder.salesOrderItem.find(
                    (item) => item.id === soi.parentSalesOrderItemId
                );
                return parentSalesOrderItem && parentSalesOrderItem.endDate;
            }

            let endDate = Utils.isDate(serviceEndDate)
                ? serviceEndDate
                : lookup(serviceEndDate);

            if (endDate) {
                if (serviceEndOffset) {
                    endDate = moment(endDate, "YYYY-MM-DD").add(
                        serviceEndOffset,
                        serviceEndOffsetPeriodUnit
                    );
                }

                return Utils.formatDate(endDate);
            }

            return null;
        };
        const salesOrderDereferenceEndDate = () =>
            searchForDateField(soi.dereferencedEndDate);
        const sspEndDate = (lookupSalesOrderItem) =>
            searchForDateField(
                ssp && ssp.deliveryEndDate,
                lookupSalesOrderItem
            );

        // In case dereference rules needs to take priority over user selected date,
        // We delete the date and attempt to apply dereference rules.
        // If the sales order is terminated, do not apply dereference rules
        let manualEndDate = undefined;
        let userSelectedEndDate = undefined;
        if (
            dereferenceRuleHasPriority &&
            soi.dereferencedEndDate === "manualDate" &&
            !salesOrder.isTerminated
        ) {
            manualEndDate = soi.endDate;
            userSelectedEndDate = soi.userSelectedEndDate;
            delete soi.endDate;
            delete soi.dereferencedEndDate;
            delete soi.userSelectedEndDate;
        }

        if (soi.dereferencedEndDate !== "manualDate") {
            // If dereference source for end date is salesOrder that can't clear it.
            // dont clear for other source, as we will recalculate it.
            if (
                soi.dereferencedEndDateSource &&
                soi.dereferencedEndDateSource !== "salesOrder"
            )
                soi.dereferencedEndDate = undefined;

            // Always reset dereference end date value as we will recalcualte it.
            delete soi.dereferencedEndDateValue;
            delete soi.endDate;

            //0: override at the sales order item level if the user has specified a value
            //to use for a de-reference lookup
            if (soi.dereferencedEndDateSource == "salesOrder") {
                soi.endDate = salesOrderDereferenceEndDate();
            } else if (ssp && ssp.deliveryEndDate) {
                //1. override at the SSP or product level if the user
                //has not specified a date
                soi.endDate = sspEndDate(true);
                soi.dereferencedEndDate = ssp.deliveryEndDate;
                soi.dereferencedEndDateSource = "ssp";
            } else if (soi.product && soi.product.serviceEndDate) {
                soi.endDate = productEndDate();
                soi.dereferencedEndDate = soi.product.serviceEndDate;
                soi.dereferencedEndDateSource = "product";
                if (
                    !soi.isCreditNote &&
                    Utils.isDate(soi.product.serviceEndDate)
                ) {
                    soi.dereferencedEndDate = "Absolute Date";
                }
            } else {
                soi.dereferencedEndDateValue = undefined;
                soi.dereferencedEndDateSource = undefined;
            }

            if (soi.endDate) {
                soi.dereferencedEndDateValue = soi.endDate;
            }
        }

        // derefence end date is manual
        if (soi.startDate && !soi.userSelectedEndDate) {
            if (soi.termSource != "salesOrder") delete soi.term;

            let term;

            if (soi.term != undefined && soi.termSource == "salesOrder") {
                // Term in sales order takes priority over dereferenced end date in product and ssp.
                if (
                    !soi.dereferencedEndDateSource ||
                    ["product", "ssp"].includes(soi.dereferencedEndDateSource)
                ) {
                    term = soi.term;
                    soi.termSource = "salesOrder";
                }
            } else if (ssp.term != undefined && ssp.term) {
                // Term in SSP takes priority over dereferenced end date in product.
                if (
                    !soi.dereferencedEndDateSource ||
                    soi.dereferencedEndDateSource == "product"
                ) {
                    term = ssp.term;
                    soi.termSource = "ssp";
                }
            } else if (
                !disableProduct &&
                soi.product.term != undefined &&
                soi.product.term
            ) {
                if (!soi.dereferencedEndDateSource) {
                    term = soi.product.term;
                    soi.termSource = "product";
                }
            }

            if (term && soi.isActive) {
                soi.term = term;

                let endDate = Utils.addMonthsAsDuration(
                    soi.startDate,
                    soi.term
                );

                if (endDateInclusive) endDate = Utils.addDays(endDate, -1);

                soi.endDate = Utils.formatDate(endDate);

                delete soi.dereferencedEndDateValue;
                delete soi.dereferencedEndDateSource;
                delete soi.dereferencedEndDate;
            }
        }

        // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
        // or from UI
        if (dereferenceRuleHasPriority && !soi.endDate && manualEndDate) {
            soi.endDate = manualEndDate;
            soi.dereferencedEndDate = "manualDate";
            soi.userSelectedEndDate = userSelectedEndDate;
        }

        if (!soi.endDate && parentRaItem) {
            const { salesOrderItem: parentSoi } = parentRaItem;
            if (parentSoi) {
                soi.endDate = parentSoi.endDate;
                soi.dereferencedEndDate = parentSoi.dereferencedEndDate;
            }
        }

        if (!soi.endDate) {
            // If recognition rule is POINT_IN_TIME than enddate is not required.
            // Otherwise mark the item inactive.
            if (ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME) {
                soi.isActive = false;
            }

            // if null endDate just delete it.
            delete soi.endDate;
        }

        // If end date was explicitly selected by user and start date is after end date make end date equal start date.
        // This can happen when user has dereferenced end date for which the value is greater than user selected end date.
        if (
            soi.userSelectedEndDate &&
            soi.endDate &&
            soi.startDate &&
            Period.isBefore(soi.endDate, soi.startDate)
        ) {
            soi.endDate = soi.startDate;
        }

        // if recognition rule is not point in time and end date is missing, throw an exception
        if (
            isApiCall &&
            !soi.endDate &&
            ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME
        ) {
            errors.add(Codes.TRAN_04, {
                salesOrder,
                soi,
                salesOrderId: salesOrder.id,
                orderDate: salesOrder.orderDate
            });
        }

        if (rai) {
            rai.isActive = soi.isActive;
            rai.deliveryStartDate = soi.startDate;
            // If there is a prospective modification on a salesOrder, populate the delivery dates based on the modification date
            if (rai.itemType == ItemType.SPLIT_UNRECOGNIZED) {
                rai.deliveryStartDate = revenueArrangement.modificationDate;
            }
            rai.deliveryEndDate = soi.endDate || soi.startDate;
            if (rai.itemType == ItemType.SPLIT_FROZEN) {
                rai.deliveryEndDate = revenueArrangement.modificationDate;
            }
            rai.billingDate = soi.billingDate;

            if (soi.transactionDate) {
                rai.transactionDate = soi.transactionDate;
            }
        }
    }
}
